import { SelectionModel } from '@angular/cdk/collections';
import { AfterContentInit, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { NavigationStart, Router } from '@angular/router';
import { filter, first, merge, Subscription } from 'rxjs';
import { CountriesEnum } from 'src/common-panel/enums/countries-enum';
import { SendindStatesEnum } from 'src/app/common/enums/sendind-states-enum';
import { UserRolesEnum } from 'src/app/common/enums/user-roles-enum';
import { UserStatusesEnum } from 'src/app/common/enums/user-statuses-enum';
import { UserListItem } from 'src/app/common/models/users-data/user-list-item.model';
import { ClinicService } from 'src/app/services/clinic.service';
import { DialogsManagerService } from 'src/app/services/dialogs-manager.service';
import { EntOfficeService } from 'src/app/services/ent-office.service';
import { UserListService } from 'src/app/services/user-list.service';
import { UserManageService } from 'src/app/services/user-manage.service';
import { SortingHeadersEnum } from './enums/sorting-headers-enum';
import { HomeTableDataSource } from './home-table-datasource';
import { SortingDirectionsEnum } from 'src/common-panel/enums/sorting-directions-enum';
import { ServerValueInterpreterService } from 'src/common-panel/services/server-value-interpreter.service';
import { COUNTRIES_IDS, CountryId } from 'src/common-panel/constants/country-configs.constants';

@Component({
    selector: 'app-home-table',
    templateUrl: './home-table.component.html',
    styleUrls: ['./home-table.component.css'],
    standalone: false
})
export class HomeTableComponent implements OnInit, AfterContentInit
{

  subscription: Subscription = new Subscription()

  @ViewChild(MatPaginator) paginator?: MatPaginator;
  @ViewChild(MatSort) sort?: MatSort;
  @ViewChild(MatTable) table?: MatTable<UserListService>;

  selection = new SelectionModel<UserListItem>(true, []);

  dataSource: HomeTableDataSource = new HomeTableDataSource(this.userListService);

  UserStatusesEnum = UserStatusesEnum
  UserRolesEnum = UserRolesEnum
  CountriesEnum = CountriesEnum
  CountriesIDs = COUNTRIES_IDS
  SendindStatesEnum = SendindStatesEnum

  displayedColumns = [
    "bulk",
    "name",
    "email",
    "role",
    "status",
    "country",
    "clinicId",
    "actions",
  ];


  constructor(private dialogsManagerService: DialogsManagerService, private elementRef: ElementRef, private router: Router, private serverValueInterpreterService: ServerValueInterpreterService,
    private userListService: UserListService, private cref: ChangeDetectorRef, private userManageService: UserManageService, private clinicService: ClinicService, private entOfficeService: EntOfficeService) { }

  ngAfterContentInit(): void
  {
    this.cref.detectChanges()
  }

  ngOnInit(): void
  {

    this.dataSource.loadUsers();

    let l_sub = this.router.events.pipe(filter(event => event instanceof NavigationStart)).subscribe(() =>
    {
      this.subscription.unsubscribe();
      l_sub.unsubscribe();
      this.dataSource.disconnect();
      this.elementRef.nativeElement.remove();
    });

    let l_sub2 = this.userListService.needsToRefresh.pipe(filter(l_val => l_val == true)).subscribe(() =>
    {
      this.refreshTable()
    })

    this.subscription.add(l_sub2)
  }


  ngOnDestroy(): void
  {
    this.subscription.unsubscribe();
  }


  ngAfterViewInit()
  {
    let l_sub = this.sort!.sortChange.subscribe(() =>
    {
      this.paginator!.pageIndex = 0;
      this.dataSource.sortDirection = this.sort!.direction as SortingDirectionsEnum;
      this.dataSource.sortingHeader = this.sort!.active as SortingHeadersEnum;
      this.dataSource.sortData()
    });

    let l_sub2 = merge(this.sort!.sortChange, this.paginator!.page).subscribe(() =>
    {
      this.loadUsersPage();
    });

    this.subscription.add(l_sub);
    this.subscription.add(l_sub2);

    this.loadUsersPage();
  }


  loadUsersPage()
  {
    this.dataSource.pageSize = this.paginator!.pageSize;
    this.dataSource.pageIndex = this.paginator!.pageIndex;
    this.dataSource?.loadUsersPage();
  }


  refreshTable()
  {
    this.dataSource?.loadUsers();
  }

  setRoleFilter(role?: UserRolesEnum)
  {

    this.dataSource.roleFilter = role;
    this.dataSource.filterData();
    this.dataSource.sortData();
    if (this.paginator) this.paginator!.pageIndex = 0;
    this.loadUsersPage();

  }

  getShownRole(): string
  {
    switch (this.userListService.tableSettings.roleFilterState)
    {
      case UserRolesEnum.ent:
        return 'Only ENTs'

      case UserRolesEnum.hcp:
        return 'Only HCPs'
      case UserRolesEnum.dal:
        return 'Only DALs'
      default:
        return 'All roles'
    }
  }

  setCountryFilter(role?: CountryId)
  {
    this.dataSource.countryFilter = role;
    this.dataSource.filterData();
    this.dataSource.sortData();
    if (this.paginator) this.paginator!.pageIndex = 0;
    this.loadUsersPage();
  }

  getShownCountry(): string
  {
    const countryFilter = this.userListService.tableSettings.countryFilterState;
    if ( countryFilter && Object.values(COUNTRIES_IDS).includes(countryFilter)) 
      return this.serverValueInterpreterService.getFullCountryNameById(countryFilter) ;

    else return 'All countries'

  }

  getFullCountryName(countryId?: CountryId): CountriesEnum | string
  {
    if (countryId) return this.serverValueInterpreterService.getFullCountryNameById(countryId)
    return ''
  }
  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected()
  {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.count;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle()
  {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  activateSelectedUsers()
  {
    this.selection.selected.forEach(l_user =>
    {
      if (l_user.status == UserStatusesEnum.inactive) this.activateUser(l_user.userId)
    })
    this.selection.clear()
  }

  deactivateSelectedUsers()
  {
    this.selection.selected.forEach(l_user =>
    {
      if (l_user.status == UserStatusesEnum.active) this.deactivateUser(l_user.userId)
    })
    this.selection.clear()
  }

  resendInvitationsForSelectedUsers()
  {
    this.selection.selected.forEach(l_user =>
    {
      if (l_user.status == UserStatusesEnum.invited) this.resendInvitation(l_user.userId)
    })
    this.selection.clear()
  }

  resendInvitation(userId: string)
  {
    this.userListService.sendInvitation(userId)
  }

  editUser(user: UserListItem)
  {
    this.userManageService.currentUser = user;
    if (user.countryId)
    {
      if (user.role != UserRolesEnum.ent)
      {
        this.clinicService.loadClinicListByCountry(user.countryId).pipe(first(l_loaded => l_loaded == true)).subscribe(() =>
        {
          this.userManageService.currentClinic = this.clinicService.clinicList.find(l_clinic => l_clinic.clinicId == user.clinicId)
          if (this.userManageService.currentClinic) this.userManageService.newUserClinicList = [this.userManageService.currentClinic.clinicId]
          this.dialogsManagerService.showEditUserDialogComponent()
        })
      } else
      {
        this.entOfficeService.loadEntOfficeListByCountry(user.countryId).pipe(first(l_loaded => l_loaded == true)).subscribe(() =>
        {
          this.userManageService.currentOffice = this.entOfficeService.entOfficeList.find(l_office => l_office.entOfficeId == user.entOfficeId)
          this.dialogsManagerService.showEditUserDialogComponent()
        })
      }
    } else
    {
      let l_user = {
        userId: user.userId,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role,
        status: user.status,
        statusDate: user.statusDate,
        lang: user.lang,
        country: user.country,
        countryId: user.countryId,
        clinicId: user.clinicId,

      }
      console.error(`User ${ JSON.stringify(l_user) } hasn't set country id.`)
      this.userManageService.clearUser();
    }


  }



  deleteUser(user: UserListItem)
  {
    this.userManageService.currentUser = user;
    if (user.countryId)
    {
      this.clinicService.loadClinicListByCountry(user.countryId).pipe(first(l_loaded => l_loaded == true)).subscribe(() =>
      {
        this.userManageService.currentClinic = this.clinicService.clinicList.find(l_clinic => l_clinic.clinicId == user.clinicId)
        this.dialogsManagerService.showDeleteUserDialogComponent()
      })
    } else
    {
      let l_user = {
        userId: user.userId,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role,
        status: user.status,
        statusDate: user.statusDate,
        lang: user.lang,
        country: user.country,
        countryId: user.countryId,
        clinicId: user.clinicId,

      }
      console.error(`User ${ JSON.stringify(l_user) } hasn't set country id.`)
      this.dialogsManagerService.showDeleteUserDialogComponent()
    }

  }

  resetPassword(user: UserListItem)
  {
    this.userManageService.currentUser = user;
    if (user.countryId)
    {
      this.clinicService.loadClinicListByCountry(user.countryId).pipe(first(l_loaded => l_loaded == true)).subscribe(() =>
      {
        this.userManageService.currentClinic = this.clinicService.clinicList.find(l_clinic => l_clinic.clinicId == user.clinicId)
        this.dialogsManagerService.showResetUserPasswordDialogComponent()
      })
    } else
    {
      let l_user = {
        userId: user.userId,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role,
        status: user.status,
        statusDate: user.statusDate,
        lang: user.lang,
        country: user.country,
        countryId: user.countryId,
        clinicId: user.clinicId,

      }
      console.error(`User ${ JSON.stringify(l_user) } hasn't set country id.`)
      this.dialogsManagerService.showResetUserPasswordDialogComponent()
    }

  }

  changeUserStatus(event: MatSlideToggleChange, user: UserListItem)
  {
    if (event.checked) this.activateUser(user.userId)
    else this.deactivateUser(user.userId)

  }

  deactivateUser(userId: string)
  {

    this.userManageService.changeUserStatus(userId, false);
  }

  activateUser(userId: string)
  {

    this.userManageService.changeUserStatus(userId, true);
  }
}
